// INC2-CONTACT: a DE profile's Connections tab (integrations design §2.10, F4): the connections
// it holds grants on — directly, through its seat, or through a team of that seat — with level,
// families and limits, and its last acts through them (the outbox). What the viewer sees is what
// they manage: a manager the company's connections; a person their own connections' grants.
// Calls on open: `connectionGrants.listAll?holder=` only (its `connections` name each grant's
// connection). The access matrix and the last acts (`outbox.list?requested_by=`) mount as the
// person scrolls to them or asks (acceptance 3: a section's load makes its own calls).
import type { OperationOutput } from "@companyos/sdk";
import { Badge, buttonVariants, cn, EmptyState, InfoTip, ListRow } from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { PlugZapIcon, SendIcon } from "lucide-react";
import type { Api } from "../../api/client";
import { keepFocus } from "../../focus/focus";
import { outboxKey, outboxSummary } from "../agents/outbox-screen";
import { type CompanyAccess, isManager } from "../company";
import { WhenNear } from "../de/when-near";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { useFeature } from "../use-feature";
import { AccessMatrixScreen, TemplateAccess } from "./access-matrix";
import {
  connectionName,
  familiesText,
  grantsQuery,
  LEVEL_INFO,
  LEVEL_LABEL,
  limitsText,
  PROVIDER_NAME,
  useGrantees,
} from "./grants";

type De = Pick<OperationOutput<"digitalEmployees.get">, "membership_id" | "display_name">;

export function DeConnectionsTab({
  api,
  company,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  de: De;
}) {
  const co = company.id;
  const grants = useQuery(grantsQuery(api, co, { holder: de.membership_id }));
  const names = useGrantees(api, co, (grants.data?.data.length ?? 0) > 0);
  const agentsOn = useFeature("agents").enabled;
  const manage = isManager(company) ? (
    <Link
      to="/c/$slug/agents/connections"
      params={{ slug: company.slug }}
      search={keepFocus}
      className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}
    >
      Manage connections
    </Link>
  ) : null;
  if (grants.isPending) return <Loading label="Loading connections" rows={2} />;
  if (grants.isError) return <LoadError what="Connections" error={grants.error} />;
  const rows = grants.data.data;
  const nameOf = (id: string) => connectionName(id, new Map(), grants.data.connections);
  return (
    <div className="flex min-w-0 flex-col gap-4" data-slot="de-connections">
      {rows.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={PlugZapIcon}
          title="No connections"
          description="No connection you manage is granted to this DE."
          actions={manage}
        />
      ) : (
        <>
          <ul aria-label="Connections" className="rounded-lg border border-border">
            {rows.map((g) => {
              const c = nameOf(g.connection_id);
              const via = names.of(g);
              const limits = limitsText(g.limits);
              return (
                <li key={g.id}>
                  <ListRow
                    data-grant={g.id}
                    lead={<PlugZapIcon aria-hidden="true" />}
                    title={c.label}
                    chips={
                      <>
                        {c.provider ? (
                          <Badge variant="outline">{PROVIDER_NAME[c.provider] ?? c.provider}</Badge>
                        ) : null}
                        {c.account ? (
                          <span className="max-md:hidden font-mono text-xs text-muted-foreground">
                            {c.account}
                          </span>
                        ) : null}
                        <Badge
                          variant={g.level === "manage" ? "accent" : "outline"}
                          title={LEVEL_INFO[g.level]}
                        >
                          {LEVEL_LABEL[g.level]}
                        </Badge>
                      </>
                    }
                    meta={familiesText(g.families)}
                    trailing={
                      <span className="flex items-center gap-2 text-xs text-muted-foreground">
                        {g.grantee_kind === "membership" ? (
                          "Direct"
                        ) : (
                          <span>
                            via {g.grantee_kind} {via.name}
                          </span>
                        )}
                        {limits ? <InfoTip label={`Limits on ${c.label}`}>{limits}</InfoTip> : null}
                      </span>
                    }
                  />
                </li>
              );
            })}
          </ul>
          {manage ? <div>{manage}</div> : null}
        </>
      )}
      {/* INC2-ACCESS: this DE's row of the access matrix, editable where you may change it */}
      <section className="flex min-w-0 flex-col gap-2" aria-label="Access">
        <WhenNear eager={false} label="Access">
          {() => (
            <>
              <h2 className="flex items-center gap-2 text-sm font-medium">
                Access
                <span className="ml-auto">
                  <TemplateAccess api={api} co={co} de={de.membership_id} />
                </span>
              </h2>
              <AccessMatrixScreen api={api} co={co} agent={de.membership_id} />
            </>
          )}
        </WhenNear>
      </section>
      {agentsOn ? (
        <WhenNear eager={false} label="Last acts">
          {() => <LastActs api={api} co={co} membershipId={de.membership_id} />}
        </WhenNear>
      ) : null}
    </div>
  );
}

/** The DE's last outward acts (the outbox), newest first. */
function LastActs({ api, co, membershipId }: { api: Api; co: string; membershipId: string }) {
  const agents = useFeature("agents");
  const q = useQuery({
    queryKey: [...outboxKey(co), "requested_by", membershipId],
    enabled: agents.enabled,
    queryFn: () =>
      api.client.outbox.list({ params: { co }, query: { requested_by: membershipId, limit: 10 } }),
  });
  if (!agents.enabled) return null;
  return (
    <section className="flex flex-col gap-2" aria-label="Last acts">
      <h3 className="flex items-center gap-1 text-sm font-medium">
        Last acts
        <InfoTip label="About last acts">
          Mail, CRM writes, messages and API calls this DE asked for, from the Outbox.
        </InfoTip>
      </h3>
      {q.isPending ? (
        <Loading label="Loading last acts" rows={2} />
      ) : q.isError ? (
        <LoadError what="Last acts" error={q.error} />
      ) : q.data.data.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={SendIcon}
          title="No acts yet"
          description="No activity for this DE that you can see."
        />
      ) : (
        <ul className="flex flex-col" data-slot="de-last-acts">
          {q.data.data.map((x) => (
            <li
              key={x.id}
              data-status={x.status}
              className="flex min-h-9 items-center gap-2 border-b border-border py-1.5 text-[13px]"
            >
              <Badge
                variant={
                  x.status === "dead" ? "danger" : x.status === "sent" ? "success" : "neutral"
                }
              >
                {x.status === "dead" ? "Dead letter" : x.status}
              </Badge>
              <span className="min-w-0 flex-1 truncate">{outboxSummary(x) || "—"}</span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {formatDateTime(x.sent_at ?? x.created_at)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
